import camelCase from 'lodash/camelCase';
import { ComponentType, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { XmlFragment as YXmlFragment } from 'yjs';

import { WidgetType } from 'shared/common/utils/widgetsUtils';
import { ContentObjectType, getContentObjectsMap } from 'shared/common/utils/yjs';
import { ManifestType } from 'shared/types/ManifestType';
import { useWidgetComponent } from 'shared/utils/useWidgetComponent';
import { useConvertObservedYXmlFragment, useObservedProperty } from 'shared/utils/yjs.client';

import { yXmlFragmentToText } from '../../../utils/tiptap/convert';
import { useContentObjectProperty } from '../../providers/ContentObjectProvider/ContentObjectProvider';
import { useCurrentDocument } from '../../yjs/YjsProvider/hooks/useCurrentDocument';
import { ConfigTitleBase } from '../ConfigTitleBase';

const configTitleSelector = (widget: ContentObjectType) => {
  const type = widget?.get('type') as WidgetType;
  const labelFragment = widget?.get('labelFragment') as YXmlFragment | undefined;
  const alt = widget?.get('alt') as string | undefined;
  const title = widget?.get('title') as string | undefined;
  const documentType = widget?.get('documentType') as string | undefined;
  const direction = widget?.get('direction') as string | undefined;

  return { type, labelFragment, title, alt, documentType, direction };
};
type ConfigTitleProps = {
  title?: string;
  Icon?: ComponentType<{ width: number; height: number }> | string | null;
};

export function ConfigTitle(props: ConfigTitleProps) {
  const { title: finalTitle, Icon } = props;
  const { t } = useTranslation('widgets');
  const [id] = useContentObjectProperty<string>('id');
  const document = useCurrentDocument();
  const [widgetData] = useObservedProperty(getContentObjectsMap(document), id, {
    deepObserve: true,
    selector: configTitleSelector
  });

  const { type, labelFragment, title, alt, documentType, direction } = widgetData;

  const labelText = useConvertObservedYXmlFragment({
    fragment: labelFragment,
    convertFunction: yXmlFragmentToText
  });

  const { Manifest: widgetManifest } = useWidgetComponent(type);

  const getActivePreview = useCallback(
    (widgetManifest: ManifestType) => {
      return (
        widgetManifest.previewGroups.find(preview => {
          // Widgets like MicrosoftEmbed have multiple divider icons. To find the correct one, it's needed to pass a property to
          // find it in previewGroups array.
          //same goes for flexSection that has different directions
          if (documentType) {
            return preview.id.toLowerCase() === documentType.toLowerCase();
          } else if (direction) {
            return preview.id.toLowerCase() === direction.toLowerCase();
          }
          return;
        }) ?? widgetManifest.previewGroups[0]
      );
    },
    [documentType, direction]
  );
  let activePreviewIcon = null;
  let activePreviewTitle = '';

  if (widgetManifest && (!finalTitle || !Icon)) {
    const activePreview = getActivePreview(widgetManifest);
    activePreviewIcon = typeof activePreview.divider !== 'boolean' ? activePreview.divider?.icon : null;
    const localeName = camelCase(type);
    activePreviewTitle = t(`${localeName}Manifest.${activePreview.id ?? localeName}`);
  }

  const DividerIcon = Icon ?? activePreviewIcon;
  const titleText = finalTitle ?? activePreviewTitle;

  const subtitle = labelText ?? finalTitle ?? title ?? alt;

  return <ConfigTitleBase Icon={DividerIcon} title={titleText} subtitle={subtitle} />;
}
